import React, { useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { API_IMAGES } from './Include'
import { Link, useLocation } from 'react-router-dom'
import { useMediaQuery } from 'react-responsive'
import './Sidebar.css'
import Icon from './IconComponent'

interface MenuItem {
  id: string;
  title: string;
  icon: string;
  path: string;
  isCompleted?: boolean;
  isDisabled?: boolean;
  stepNumber?: number;
  submenu?: MenuItem[];
}

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation()
  const [expandedMenus, setExpandedMenus] = useState<string[]>([])
  const sidebarRef = useRef<HTMLDivElement>(null)
  const isMobile = useMediaQuery({ maxWidth: 991 })

  const currentPath = location.pathname

  const menuItems: MenuItem[] = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: 'dashboard',
      path: '/app/dashboard'
    },
    {
      id: 'skills',
      title: 'Register Course',
      icon: 'book',
      path: '/app/skills'
    },
     {
      id: 'courses-change',
      title: 'Change Course',
      icon: 'question-circle',
      path: '/app/portal/change-course'
    },
    {
      id: 'courses',
      title: 'Change Skill',
      icon: 'question-circle',
      path: '/app/portal/change-skill'
    },
    {
      id: 'schedule',
      title: 'Request Certificate',
      icon: 'calendar',
      path: '/app/portal/certificate'
    },
    {
      id: 'complaint',
      title: 'Drop Complaints',
      icon: 'user',
      path: '/app/portal/complaints'
    }
  ]

  const toggleSubmenu = (id: string) => {
    setExpandedMenus(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    )
  }

  const isActive = (path: string) => {
    if (path === '#') return false
    return currentPath === path || currentPath.startsWith(`${path}/`)
  }

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isMobile &&
        isOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isMobile, isOpen, onClose])

  const renderMenuItem = (item: MenuItem) => {
    const isMenuActive = isActive(item.path)
    const hasSubmenu = item.submenu && item.submenu.length > 0
    const isExpanded = expandedMenus.includes(item.id)

    return (
      <li
        key={item.id}
        className={`sidebar-item ${isMenuActive ? 'active' : ''} ${item.isDisabled ? 'disabled' : ''}`}
      >
        {item.isDisabled ? (
          <div className="sidebar-link disabled">
            <div className="sidebar-link-content">
              {item.stepNumber && <span className="step-number">{item.stepNumber}</span>}
              <span className="sidebar-icon"><Icon name={item.icon} size={16} /></span>
              <span className="sidebar-title">{item.title}</span>
            </div>
            {item.isCompleted && <span className="completed-icon">✓</span>}
          </div>
        ) : hasSubmenu ? (
          <div
            className={`sidebar-link ${isExpanded ? 'expanded' : ''}`}
            onClick={() => toggleSubmenu(item.id)}
          >
            <div className="sidebar-link-content">
              <span className="sidebar-icon"><Icon name={item.icon} size={16} /></span>
              <span className="sidebar-title">{item.title}</span>
            </div>
            <span className="submenu-toggle">
              {isExpanded
                ? <Icon name="chevron-down" size={12} />
                : <Icon name="chevron-right" size={12} />}
            </span>
          </div>
        ) : (
          <Link to={item.path} className="sidebar-link">
            <div className="sidebar-link-content">
              {item.stepNumber && <span className="step-number">{item.stepNumber}</span>}
              <span className="sidebar-icon"><Icon name={item.icon} size={16} /></span>
              <span className="sidebar-title">{item.title}</span>
            </div>
            {item.isCompleted && <span className="completed-icon">✓</span>}
          </Link>
        )}

        {hasSubmenu && isExpanded && (
          <ul className="submenu">
            {item.submenu!.map(subItem => (
              <li
                key={subItem.id}
                className={`submenu-item ${isActive(subItem.path) ? 'active' : ''}`}
              >
                <Link to={subItem.path} className="submenu-link">
                  <span className="submenu-icon"><Icon name={subItem.icon} size={16} /></span>
                  <span className="submenu-title">{subItem.title}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </li>
    )
  }

  return (
    <>
      <aside
        className={`sidebar-wrapper no-print ${isOpen ? 'sidebar-open' : 'sidebar-closed'}`}
        ref={sidebarRef}
      >
        <div className="sidebar-inner">
          <div className="sidebar-header">
            <Link to="/" className="sidebar-logo">
              <img src={API_IMAGES + '/edclogo.png'} className="logo-large" alt="EDC logo" />
              <img src={API_IMAGES + '/edc.jpg'} className="logo-small" alt="EDC logo" />
            </Link>
          </div>

          <div className="sidebar-content">
            <nav className="sidebar-nav">
              <ul className="sidebar-menu">
                {menuItems.map(item => renderMenuItem(item))}
              </ul>
            </nav>
          </div>

          <div className="sidebar-footer">
            <Link to="/logout" className="logout-button">
              <Icon name="sign-out" size={16} />
              <span>Log Out</span>
            </Link>
          </div>
        </div>
      </aside>

      {/* Dark overlay — only on mobile when sidebar is open */}
      {isMobile && isOpen && (
        <div className="sidebar-overlay" onClick={onClose} />
      )}
    </>
  )
}

export default Sidebar

declare global {
  interface WindowEventMap {
    'toggle-sidebar': CustomEvent
    'sidebar-state-changed': CustomEvent
  }
}
